// Option-led booking (Booking V3).
//
// For most services the TOP ROW is the whole booking — date, duration, party —
// and the options underneath are flavours of it. A wine flight is the classic
// case: the party is 4, they split across flights, one shared window.
//
// A service backed by attributed stock is the other way round. A ride, a
// simulator bay, a rig: THE OPTION is the booking. It has its own length (a
// 90-minute meadow ride, a 2-hour ridge ride) and its own headcount (one horse
// per rider, so "2 on Easy Meadow + 4 on Ridge" is two bookings of 2 and 4,
// not a party of 6 that "picked 8").
//
// Tenants opt a service in through `services.config`, already in the widget
// payload, so nothing changes for anyone who hasn't:
//
//   option_led: true            WidgetShell mounts the availability panel
//                               (components/AvailabilityPanel.tsx): what →
//                               how many → when, with the days and start
//                               times answered for the whole choice by
//                               get_service_availability_bands.
//   option_selection: "single"  the options are variants of ONE thing (60/90/
//                               120-minute sessions) — pick one and step its
//                               headcount, capped by party_max / the option's
//                               capacity. Default: multi — the options are
//                               different things (rides) and each ticked one
//                               has its own headcount.
//   start_granularity, guest_noun, length_labels — read by the helpers below.
//
// Each option's length lives in `service_units.config.duration_minutes`
// (get_widget_config allow-lists it); an option priced by length
// (units[].price_tiers) with no fixed length lets the guest choose one. Falls
// back to the service's duration.

import type { WidgetService, WidgetUnit } from "../types";
import { bookingDurationMinutes } from "@vertu/shared";
import { isServiceBookable } from "./capacity";
import { formatLength, type Granularity, type SelectionEntry } from "./availability-logic";

function cfg(service: Pick<WidgetService, "config">): Record<string, unknown> {
  const c = service.config;
  return c && typeof c === "object" ? (c as Record<string, unknown>) : {};
}

export function isOptionLed(service: Pick<WidgetService, "config">): boolean {
  return cfg(service).option_led === true;
}

export function optionSelectionSingle(
  service: Pick<WidgetService, "config">,
): boolean {
  return cfg(service).option_selection === "single";
}

/**
 * The party ceiling for an option-led single-select option. `config.party_max`
 * on the SERVICE wins (set together with the engine's guests_per_unit — a
 * bay seats 4, two bays serve 8); otherwise the option's own capacity. Never
 * services.max_capacity, which predates the multi-unit model.
 */
export function partyMaxFor(
  service: Pick<WidgetService, "config">,
  unit: Pick<WidgetUnit, "capacity"> | null | undefined,
): number | null {
  const pm = cfg(service).party_max;
  if (typeof pm === "number" && Number.isFinite(pm) && pm > 0) return Math.round(pm);
  const cap = unit?.capacity ?? null;
  return cap != null && cap > 0 ? cap : null;
}

/**
 * The party FLOOR for an option: the fewest guests one booking of it takes
 * (units[].party_size_min — "2–4 riders required"). Only a per-person option
 * has one: a per-item option holds a party of one per item, so a minimum
 * there would refuse every line. The server applies the same rule
 * (_booking_option_party_min) and refuses a smaller hold with
 * party_below_minimum; this only keeps the steppers from offering it.
 * 1 when there is none.
 */
export function partyMinFor(
  service: Pick<WidgetService, "pricing_basis">,
  unit: Pick<WidgetUnit, "party_size_min" | "pricing_basis"> | null | undefined,
): number {
  if (!unit) return 1;
  const basis = unit.pricing_basis ?? ((service.pricing_basis ?? "per_unit") === "per_person" ? "per_person" : "per_item");
  if (basis !== "per_person") return 1;
  const min = unit.party_size_min;
  return typeof min === "number" && Number.isFinite(min) && min >= 2 ? Math.floor(min) : 1;
}

/** The option's own length, or null when it inherits the service's. */
export function unitDurationMinutes(
  unit: Pick<WidgetUnit, "config">,
): number | null {
  const c = unit.config;
  if (!c || typeof c !== "object") return null;
  const d = (c as Record<string, unknown>).duration_minutes;
  return typeof d === "number" && Number.isFinite(d) && d > 0
    ? Math.round(d)
    : null;
}

/** When a hold of `unit` starting at `startsAt` ends. */
export function optionEndsAt(
  service: Pick<WidgetService, "config" | "service_type" | "duration_minutes">,
  unit: Pick<WidgetUnit, "config" | "price_tiers">,
  startsAt: string,
  selectedMinutes?: number,
): string {
  return new Date(Date.parse(startsAt) + optionDurationMinutes(service, unit, selectedMinutes) * 60000).toISOString();
}

/** Variable options expose their configured duration prices as choices. */
export function optionDurationChoices(unit: Pick<WidgetUnit, "config" | "price_tiers">): number[] {
  if (unitDurationMinutes(unit) != null) return [];
  return [...new Set((unit.price_tiers ?? []).map((tier) => tier.duration_minutes)
    .filter((minutes) => Number.isFinite(minutes) && minutes > 0))].sort((a, b) => a - b);
}

export function optionDurationMinutes(
  service: Pick<WidgetService, "config" | "service_type" | "duration_minutes">,
  unit: Pick<WidgetUnit, "config" | "price_tiers">,
  selectedMinutes?: number,
): number {
  const fixed = unitDurationMinutes(unit);
  if (fixed != null) return fixed;
  const choices = optionDurationChoices(unit);
  return selectedMinutes != null && choices.includes(selectedMinutes)
    ? selectedMinutes : choices[0] ?? bookingDurationMinutes(service);
}

// ─── Availability panel helpers (Booking V3) ────────────────────────────────
// Added beside the exports above, which e2e/booking-v3-guest-logic.spec.ts pins
// (their names, parameters and precedence must not change). Everything below
// is pure; components import it from "../state/option-led" by path, never the
// @vertu/shared index, whose optionDurationMinutes has another signature.

/** get_service_availability_bands takes 1..4 distinct units per visit (20260917090020). */
export const MAX_ENTRIES = 4;

/** The guest count the stepper allows when nothing else sets a ceiling. */
export const DEFAULT_PARTY_CAP = 20;

export type OptionShape = "sessions" | "multi";

/** Sessions = variants of one thing (pick one + a headcount); multi = tick several, each with its own count. */
export function optionShape(service: Pick<WidgetService, "config">): OptionShape {
  return optionSelectionSingle(service) ? "sessions" : "multi";
}

export type PriceTier = { duration_minutes: number; price: number };

/** The unit's valid length prices, ascending by length, one per length (first wins). */
export function unitPriceTiers(unit: Pick<WidgetUnit, "price_tiers">): PriceTier[] {
  const seen = new Set<number>();
  const out: PriceTier[] = [];
  for (const t of unit.price_tiers ?? []) {
    const m = t?.duration_minutes;
    const p = t?.price;
    if (typeof m !== "number" || !Number.isInteger(m) || m <= 0) continue;
    if (typeof p !== "number" || !Number.isFinite(p) || p < 0) continue;
    if (seen.has(m)) continue;
    seen.add(m);
    out.push({ duration_minutes: m, price: p });
  }
  return out.sort((a, b) => a.duration_minutes - b.duration_minutes);
}

/** The unit's price for exactly `minutes` (rand), or null when it has no such length. */
export function tierPrice(unit: Pick<WidgetUnit, "price_tiers">, minutes: number | null): number | null {
  if (minutes == null) return null;
  return unitPriceTiers(unit).find((t) => t.duration_minutes === minutes)?.price ?? null;
}

/** How many guests one option takes (multi): its capacity, else the default cap. */
export function unitCap(unit: Pick<WidgetUnit, "capacity">): number {
  const c = unit.capacity;
  return typeof c === "number" && c > 0 ? c : DEFAULT_PARTY_CAP;
}

/** The session headcount ceiling: partyMaxFor, else the default cap. */
export function partyCapFor(
  service: Pick<WidgetService, "config">,
  unit: Pick<WidgetUnit, "capacity"> | null | undefined,
): number {
  return partyMaxFor(service, unit) ?? DEFAULT_PARTY_CAP;
}

function positiveInt(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : null;
}

/** Large-party policy "block": the hold refuses a party above this, so no stepper goes past it. */
export function oversizeBlockCap(
  service: Pick<WidgetService, "party_size_max_auto" | "oversize_party_policy">,
): number | null {
  return service.oversize_party_policy === "block" ? positiveInt(service.party_size_max_auto) : null;
}

/** Large-party policy "enquiry": a party above this books by enquiry, not a hold. */
export function oversizeEnquiryAbove(
  service: Pick<WidgetService, "party_size_max_auto" | "oversize_party_policy">,
): number | null {
  return service.oversize_party_policy === "enquiry" ? positiveInt(service.party_size_max_auto) : null;
}

/** services.config.start_granularity: 15 | "any" (strict literals), else 30. */
export function startGranularity(service: Pick<WidgetService, "config">): Granularity {
  const g = cfg(service).start_granularity;
  return g === 15 ? 15 : g === "any" ? "any" : 30;
}

/** services.config.guest_noun {one, other}, else guest/guests. */
export function guestNoun(service: Pick<WidgetService, "config">): { one: string; other: string } {
  const n = cfg(service).guest_noun;
  if (n && typeof n === "object") {
    const { one, other } = n as Record<string, unknown>;
    if (typeof one === "string" && one.trim() && typeof other === "string" && other.trim()) {
      return { one: one.trim(), other: other.trim() };
    }
  }
  return { one: "guest", other: "guests" };
}

/**
 * How many guests an option takes, as its card says it: "2–4 riders" when
 * both ends are known, "At least 2 riders" with only a floor, null when there
 * is no floor (the card keeps its "Up to N").
 */
export function partyRangeLabel(
  min: number,
  max: number | null,
  noun: { one: string; other: string },
): string | null {
  if (min < 2) return null;
  if (max != null && max >= min) return max === min ? `${min} ${noun.other}` : `${min}–${max} ${noun.other}`;
  return `At least ${min} ${noun.other}`;
}

/** services.config.length_labels["240"] ("Half day"), else "4 h". */
export function lengthLabel(service: Pick<WidgetService, "config">, minutes: number): string {
  const labels = cfg(service).length_labels;
  if (labels && typeof labels === "object") {
    const v = (labels as Record<string, unknown>)[String(minutes)];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return formatLength(minutes);
}

/**
 * p_selection for the guest's choice, in service.units (payload) order — the
 * order the widget will place the holds in. `counts` maps unit id → guests
 * (sessions: the one picked unit); `minutes` maps a variable-length unit to
 * its chosen length. A unit with its own fixed length sends minutes null (the
 * engine applies it); every other unit sends its length explicitly, so the
 * band and the hold can never fall back to different lengths. Null when
 * nothing is chosen.
 */
export function buildEntries(
  service: Pick<WidgetService, "config" | "service_type" | "duration_minutes" | "units">,
  choice: {
    counts: Readonly<Record<string, number>>;
    minutes?: Readonly<Record<string, number>>;
  },
): SelectionEntry[] | null {
  const out: SelectionEntry[] = [];
  for (const unit of service.units) {
    const raw = choice.counts[unit.id];
    if (typeof raw !== "number" || !Number.isFinite(raw) || raw < 1) continue;
    out.push({
      service_unit_id: unit.id,
      guests: Math.min(100, Math.floor(raw)),
      minutes:
        unitDurationMinutes(unit) != null
          ? null
          : optionDurationMinutes(service, unit, choice.minutes?.[unit.id]),
    });
  }
  return out.length ? out : null;
}

/**
 * Whether WidgetShell mounts the availability panel for this service. An
 * option-led service that is not bookable, or is dining / accommodation /
 * events, or runs special-date events, keeps ServicePanel, whose early return
 * renders ServiceUnavailable / EventVenuePanel before any option logic. So
 * does one with hosted events (`hostedEventCount`, the shell's events linked
 * to the service): the panel does not render hosted-event cards, and routing
 * it there would hide them.
 */
export function routesToAvailabilityPanel(service: WidgetService, hostedEventCount = 0): boolean {
  return (
    isOptionLed(service) &&
    isServiceBookable(service) &&
    !["dining", "accommodation", "events"].includes(service.service_type) &&
    (service.special_date_events ?? []).length === 0 &&
    hostedEventCount === 0
  );
}
